import { ButtonHTMLAttributes, useEffect, useRef, useState } from 'react'
import Flip from 'gsap/dist/Flip'

import Button from '@/components/base/button'
import ButtonMask from '@/components/base/button-mask'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'

export type PlayButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

const PlayButton = ({ children, ...props }: PlayButtonProps) => {
  const [active, setActive] = useState(false)
  const rect = useRef<HTMLSpanElement>(null!)
  const wrapper = useRef<HTMLSpanElement>(null!)

  useEffect(() => {
    const state = Flip.getState(rect.current)
    wrapper.current.classList[active ? 'add' : 'remove']('active')
    Flip.from(state, { duration: 0.5, ease: 'power2.out' })
  }, [active])

  return (
    <Button
      variant="secondary"
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      endIcon={
        <span ref={wrapper} className="relative [&.active]:static">
          <span
            ref={rect}
            className="absolute left-0 top-0 !m-0 size-full rounded-[0.5rem] bg-dark dark:bg-white"
          />
          <svg className="relative size-[2.6rem]" viewBox="0 0 26 26" fill="none">
            <rect
              className="fill-dark dark:fill-white"
              x="0.5"
              y="0.5"
              width="25"
              height="25"
              rx="2"
            />
            <rect
              className={cn(
                'fill-white dark:fill-dark',
                'transition-transform duration-500 ease-out-cubic',
                'origin-center scale-0 group-hover/button:scale-100'
              )}
              x="0.5"
              y="0.5"
              width="25"
              height="25"
              rx="2"
            />
            <path
              d="M17.75 12.067C18.0833 12.2594 18.0833 12.7406 17.75 12.933L10.25 17.2631C9.91667 17.4556 9.5 17.215 9.5 16.8301V8.16987C9.5 7.78497 9.91667 7.54441 10.25 7.73686L17.75 12.067Z"
              className={cn(
                'fill-white dark:fill-dark',
                'origin-center transition-all duration-300 ease-out-cubic',
                'group-hover/button:fill-dark dark:group-hover/button:fill-white'
              )}
            />
          </svg>
        </span>
      }
      {...props}
    >
      <ButtonMask className="z-10 after:text-white dark:after:text-dark">
        <Typography variant="button">{children}</Typography>
      </ButtonMask>
    </Button>
  )
}

export default PlayButton
